import { alpha, Box, ListItem, Typography, useTheme } from '@mui/material';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';

import { ACTOR_ROUTES, DIRECTOR_ROUTES, ROUTES } from '@/shared/constants';

import { useGetMenuCounters } from '@/entities/menu-counters';
import { NotificationsBadge } from '@/entities/notifications';

import { INavItem } from '../model/types';

const ICON_SIZE = 24;

/**
 * Элемент навгиации меню
 * @param root0 Пропсы
 * @param root0.Icon Иконка кнопки
 * @param root0.path Путь для перехода
 * @param root0.activePathPrefixes Префиксы путей для активного состояния
 * @param root0.isFillIcon Красим ли иконку через fill или через stroke
 * @param root0.label Текст элемента
 * @param root0.isExpanded Расширен ли сайдбар
 */
export const NavItem = ({
  Icon,
  path,
  activePathPrefixes,
  isFillIcon,
  label,
  isExpanded
}: INavItem) => {
  const { palette } = useTheme();
  const { pathname } = useLocation();

  const navigate = useNavigate();

  // TODO: Сейчас запрос за счётчиками делается при переключении каждой страницы в сайдбаре,
  // чтобы получать обновленное количество. Когда получение будет через вебсокеты - поправить
  const { menuCounters } = useGetMenuCounters();

  // path → счётчик бейджа. Ключи, которых нет в ответе (undefined), бейдж не рисуют;
  // API отдаёт только роль-специфичные ключи, поэтому актёрская «Мои отклики» (тот же
  // путь '/responses') не подхватит режиссёрский responses_pending. invitations_pending
  // намеренно НЕ добавляем — решение по бейджу приглашений отложено.
  const badgeCounts: Record<string, number | undefined> = {
    [ROUTES.notifications]: menuCounters?.notifications_unviewed,
    [ACTOR_ROUTES.actor_cards.index]: menuCounters?.actor_cards_pending,
    [DIRECTOR_ROUTES.responses.index]: menuCounters?.responses_pending
  };

  const badgeCount = badgeCounts[path];

  /**
   * Получение стилей иконки
   * @param isActive Активен ли раздел
   */
  const getStyle = (isActive: boolean) => {
    const color = isActive ? 'url(#icon-gradient)' : palette.common.white;
    const key = isFillIcon ? 'fill' : 'stroke';

    return {
      [key]: color,
      width: ICON_SIZE,
      height: ICON_SIZE,
      minWidth: ICON_SIZE,
      minHeight: ICON_SIZE,
      transition: 'all 0.3s ease-in-out',
      // Большинство иконок красят path через невалидный stroke/fill="current"
      // (браузер его игнорирует и path наследует stroke/fill родителя выше).
      // Но часть иконок (Favorites/Star, 18169 «Мои папки») использует валидный
      // stroke="currentColor" — он резолвится в computed color САМОГО path, а
      // не в stroke/fill родителя, и без явного правила красится в дефолтный
      // цвет ссылки. Реальное CSS-правило по селектору бьёт presentation-
      // атрибут независимо от того, что в нём — красим path напрямую тем же
      // значением, что и корень (работает и для url(#icon-gradient)).
      '& path': {
        [key]: color
      }
    };
  };

  /**
   * Событие при клике на ссылку
   * @param e - событие браузера
   */
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  /**
   * Проверяет активность пункта меню.
   * @param navLinkActive Активность из NavLink
   */
  const isItemActive = (navLinkActive: boolean) => {
    if (!activePathPrefixes?.length) return navLinkActive;

    return activePathPrefixes.some(prefix =>
      prefix === ACTOR_ROUTES.events.index.route
        ? pathname === prefix
        : pathname === prefix || pathname.startsWith(`${prefix}/`)
    );
  };

  return (
    <NavLink
      to={path}
      onClick={handleClick}
      style={{
        textDecoration: 'none',
        width: '100%'
      }}
    >
      {({ isActive }) => {
        const active = isItemActive(isActive);

        return (
          <ListItem
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              padding: '8px 12px',
              transition: 'all 0.3s ease-in-out',
              margin: '0 8px',
              width: 'calc(100% - 16px)',
              borderRadius: 2,
              '&:hover': {
                backgroundColor: alpha(palette.common.white, 0.12)
              },
              position: 'relative'
            }}
          >
            {/* Маска с градиентом, для применения градиента как фона у SVG */}
            <svg width='0' height='0'>
              <defs>
                <radialGradient
                  id='icon-gradient'
                  cx='0'
                  cy='0'
                  r='1'
                  gradientUnits='userSpaceOnUse'
                  gradientTransform='translate(21.2593 -5.18449) rotate(118.85) scale(36.5035 81.092)'
                >
                  <stop stopColor={palette.secondary.light} />
                  <stop offset='0.32' stopColor={palette.secondary.main} />
                  <stop offset='1' stopColor={palette.secondary.dark} />
                </radialGradient>
              </defs>
            </svg>
            {badgeCount !== undefined ? (
              <NotificationsBadge count={badgeCount}>
                <Box component={Icon} sx={getStyle(active)} />
              </NotificationsBadge>
            ) : (
              <Box component={Icon} sx={getStyle(active)} />
            )}
            {isExpanded && (
              <Typography
                variant='subtitle1'
                sx={{
                  textWrap: 'nowrap',
                  overflow: 'hidden',
                  background: active
                    ? `radial-gradient(572.63% 182.49% at 96.25% -35.94%,
                    ${palette.secondary.light} 0%,
                    ${palette.secondary.main} 31.999999284744263%,
                    ${palette.secondary.dark} 100%)`
                    : 'none',
                  WebkitBackgroundClip: active ? 'text' : 'none',
                  WebkitTextFillColor: active
                    ? 'transparent'
                    : palette.common.white,
                  opacity: 0,
                  animation: isExpanded
                    ? 'fadeIn 0.3s ease-in-out forwards'
                    : 'none',
                  '@keyframes fadeIn': {
                    from: { opacity: 0 },
                    to: { opacity: 1 }
                  },
                  textDecoration: 'none',
                  textAlign: 'left',
                  textOverflow: 'ellipsis',
                  position: 'absolute',
                  left: '64px',
                  right: '20px'
                }}
              >
                {label}
              </Typography>
            )}
          </ListItem>
        );
      }}
    </NavLink>
  );
};
